/**
 * Narration Script Editor + Pronunciation Preview + Slide Editor stage
 * components for the KAPE Course Editor.
 *
 * Workflow (Phase 7D):
 *   Generate script → Edit → Preview selection (pronunciation) →
 *   Save → Generate audio → Approve
 * Audio is NEVER auto-generated after script generation.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";
import {
  useKapeSlidesJob,
  useKapeNarrationScriptJob,
  useKapeNarrationAudioJob,
  useKapeImagePopulateJob,
  jobStatusLabel,
} from "./_useKapeJob";
import PreviewAsStudentButton from "./PreviewAsStudentButton";

// ═══════════════════════════════════════════════════════════════════
// Narration Script Editor
// ═══════════════════════════════════════════════════════════════════

export function NarrationScriptEditor({ course, lesson, reload }) {
  const [script, setScript] = useState(lesson.narration_script || "");
  const [welcome, setWelcome] = useState(lesson.welcome_script || "");
  const [closing, setClosing] = useState(lesson.closing_script || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewingText, setPreviewingText] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [processedText, setProcessedText] = useState("");
  const [showProcessed, setShowProcessed] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [versions, setVersions] = useState([]);
  const textareaRef = useRef(null);

  useEffect(() => {
    setScript(lesson.narration_script || "");
    setWelcome(lesson.welcome_script || "");
    setClosing(lesson.closing_script || "");
    setDirty(false);
  }, [lesson.narration_script, lesson.welcome_script, lesson.closing_script]);

  const loadVersions = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/kcis/admin/kape/courses/${course.id}/versions/narration_script/${lesson.id}`
      );
      setVersions(data.items || []);
    } catch { /* ignore */ }
  }, [course.id, lesson.id]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/narration-script`,
        { narration_script: script, summary: "Manual edit" }
      );
      // Also persist welcome + closing on the lesson itself
      await api.patch(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}`,
        { welcome_script: welcome, closing_script: closing, summary: "Welcome/closing edit" }
      );
      toast.success("Narration saved");
      setDirty(false);
      await Promise.all([reload(), loadVersions()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const showSpokenOnly = async () => {
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/narration-processed-text`,
        { text: script }
      );
      setProcessedText(data.tts_text || "");
      setShowProcessed(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const scriptJob = useKapeNarrationScriptJob({
    courseId: course.id, lessonId: lesson.id,
    onComplete: async () => { toast.success("Script regenerated"); await reload(); },
  });
  const audioJob = useKapeNarrationAudioJob({
    courseId: course.id, lessonId: lesson.id,
    onComplete: async () => { toast.success("Narration audio rendered"); await reload(); },
  });

  const previewSelection = async () => {
    const ta = textareaRef.current;
    if (!ta) return;
    let text = ta.value.slice(ta.selectionStart, ta.selectionEnd).trim();
    if (!text) text = script.split(/\n\n/)[0] || script.slice(0, 400);
    if (!text) return toast.error("Nothing to preview");
    setPreviewingText(text);
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/narration-preview`,
        { text }
      );
      setPreviewUrl(data.audio_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const regenerateParagraph = async () => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart, end = ta.selectionEnd;
    const paragraph = script.slice(start, end).trim();
    if (!paragraph) return toast.error("Select a paragraph first");
    setRegenerating(true);
    try {
      const { data } = await api.post(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/narration-script/regenerate-paragraph`,
        { paragraph, instruction: "Rewrite in the instructor's voice, keeping meaning intact." }
      );
      const next = script.slice(0, start) + data.paragraph + script.slice(end);
      setScript(next); setDirty(true);
      toast.success("Paragraph regenerated — remember to Save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regeneration failed");
    } finally { setRegenerating(false); }
  };

  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`narration-editor-${lesson.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>Narration script</div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => scriptJob.start()} disabled={scriptJob.isActive}
                  className="text-xs rounded-full px-3 py-1"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid={`narration-regen-full-${lesson.id}`}>
            {scriptJob.isActive ? `Generating (${jobStatusLabel(scriptJob.job)})` : (script ? "Regenerate full script" : "Generate script")}
          </button>
          <button onClick={regenerateParagraph} disabled={regenerating}
                  className="text-xs rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid={`narration-regen-para-${lesson.id}`}>
            {regenerating ? "Regenerating…" : "Regenerate selection"}
          </button>
          <button onClick={previewSelection}
                  className="text-xs rounded-full px-3 py-1"
                  style={{ background: "#7C3AED", color: "white" }}
                  data-testid={`narration-preview-${lesson.id}`}>
            Preview pronunciation
          </button>
          <button onClick={save} disabled={!dirty || saving}
                  className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid={`narration-save-${lesson.id}`}>
            {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: PAL.muted }}>
            Lesson welcome (spoken first)
          </div>
          <textarea value={welcome}
                    onChange={(e) => { setWelcome(e.target.value); setDirty(true); }}
                    rows={3}
                    className="w-full rounded bg-transparent border p-2 text-sm"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    placeholder="Welcome — I'm Dr. Christy Vonta. Today we…"
                    data-testid={`narration-welcome-${lesson.id}`} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: PAL.muted }}>
            Lesson closing (spoken last)
          </div>
          <textarea value={closing}
                    onChange={(e) => { setClosing(e.target.value); setDirty(true); }}
                    rows={3}
                    className="w-full rounded bg-transparent border p-2 text-sm"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    placeholder="Heal the root. Empower the vision. Support the structure."
                    data-testid={`narration-closing-${lesson.id}`} />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <div className="text-[10px] uppercase tracking-widest" style={{ color: PAL.muted }}>
          Narration script (production commands like [Pause], [Next Slide], [Pronounce: X] are converted to timing metadata and never spoken)
        </div>
        <button onClick={showSpokenOnly} className="ml-auto text-[10px] px-2 py-0.5 rounded border"
                style={{ borderColor: PAL.border, color: PAL.accent }}
                data-testid={`narration-show-spoken-${lesson.id}`}>
          Preview spoken text only
        </button>
      </div>
      <textarea ref={textareaRef} value={script}
                onChange={(e) => { setScript(e.target.value); setDirty(true); }}
                rows={14}
                className="w-full rounded-lg bg-transparent border p-3 text-sm font-mono outline-none"
                style={{ borderColor: PAL.border, color: PAL.text }}
                placeholder="Generate or write the narration script here…"
                data-testid={`narration-textarea-${lesson.id}`} />
      {showProcessed && processedText && (
        <div className="mt-3 rounded-lg p-3" style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
             data-testid={`narration-spoken-preview-${lesson.id}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-widest" style={{ color: PAL.accent }}>
              This is exactly what ElevenLabs will speak
            </div>
            <button onClick={() => setShowProcessed(false)} className="text-[10px]" style={{ color: PAL.muted }}>Hide</button>
          </div>
          <div className="text-xs whitespace-pre-wrap" style={{ color: PAL.text }}>{processedText}</div>
        </div>
      )}
      {previewingText && previewUrl && (
        <div className="mt-3 rounded-lg p-3" style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
             data-testid={`narration-preview-block-${lesson.id}`}>
          <div className="text-xs mb-2" style={{ color: PAL.muted }}>
            Preview: <span className="italic">&quot;{previewingText.slice(0, 120)}{previewingText.length > 120 ? "…" : ""}&quot;</span>
          </div>
          <audio src={previewUrl} controls className="w-full" />
        </div>
      )}
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <button onClick={() => audioJob.start()}
                disabled={audioJob.isActive || !script.trim() || dirty}
                className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
                style={{ background: "#7C3AED", color: "white" }}
                data-testid={`narration-gen-audio-${lesson.id}`}>
          {audioJob.isActive ? `Rendering audio (${jobStatusLabel(audioJob.job)})` : (lesson.narration?.generated ? "Regenerate audio" : "Generate full audio")}
        </button>
        {dirty && <span className="text-xs" style={{ color: "#F59E0B" }}>Save the script before rendering audio.</span>}
      </div>
      {audioJob.isFailed && (
        <div className="mt-2 text-xs" style={{ color: "#F87171" }}>
          {audioJob.job?.error_message_safe} · ref {audioJob.job?.id?.slice(0, 8)}
          <button onClick={audioJob.retry} className="ml-2 underline">Retry</button>
        </div>
      )}
      {versions.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs cursor-pointer" style={{ color: PAL.accent }}
                    data-testid={`narration-versions-${lesson.id}`}>
            Version history ({versions.length})
          </summary>
          <ul className="text-xs mt-2 space-y-1">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-2">
                <span className="font-mono" style={{ color: PAL.muted }}>v{v.version_number}</span>
                <span>{v.edited_at?.slice(0, 16)}</span>
                <span style={{ color: PAL.muted }}>{v.source}</span>
                <button onClick={async () => {
                  try {
                    await api.post(`/kcis/admin/kape/courses/${course.id}/versions/${v.id}/restore`);
                    toast.success(`Restored v${v.version_number}`);
                    await reload();
                    await loadVersions();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
                }} className="text-[10px] underline" style={{ color: PAL.accent }}
                        data-testid={`narration-restore-${v.id}`}>
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Slide Editor
// ═══════════════════════════════════════════════════════════════════

export function SlideEditor({ course, lesson, reload }) {
  const [slides, setSlides] = useState(lesson.slides || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSlides(lesson.slides || []); setDirty(false); }, [lesson.slides]);

  const patchSlide = (idx, patch) => {
    const next = slides.map((s, i) => i === idx ? { ...s, ...patch } : s);
    setSlides(next); setDirty(true);
  };
  const insertSlideAfter = (idx) => {
    const next = [...slides];
    next.splice(idx + 1, 0, { title: "New slide", bullets: [], speaker_notes: "" });
    next.forEach((s, i) => s.order = i + 1);
    setSlides(next); setDirty(true);
  };
  const deleteSlide = (idx) => {
    const next = slides.filter((_, i) => i !== idx);
    next.forEach((s, i) => s.order = i + 1);
    setSlides(next); setDirty(true);
  };
  const move = (idx, dir) => {
    const next = [...slides];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((s, i) => s.order = i + 1);
    setSlides(next); setDirty(true);
  };
  const duplicate = (idx) => {
    const next = [...slides];
    next.splice(idx + 1, 0, { ...slides[idx], bullets: [...(slides[idx].bullets || [])] });
    next.forEach((s, i) => s.order = i + 1);
    setSlides(next); setDirty(true);
  };

  const slidesJob = useKapeSlidesJob({
    courseId: course.id, lessonId: lesson.id,
    onComplete: async () => { toast.success("Slides generated"); await reload(); },
  });
  const imagesJob = useKapeImagePopulateJob({
    courseId: course.id, lessonId: lesson.id,
    onComplete: async () => { toast.success("Slide visuals populated — review each slide"); await reload(); },
  });

  const slideAction = async (slide_id, action, payload = {}) => {
    try {
      const { data } = await api.patch(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/slides/${slide_id}/visual`,
        { action, ...payload }
      );
      const next = slides.map((s) => s.id === slide_id ? data.slide : s);
      setSlides(next);
      await reload();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      throw e;
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(
        `/kcis/admin/kape/courses/${course.id}/lessons/${lesson.id}/slides`,
        { slides, summary: "Slide edit" }
      );
      toast.success("Slides saved");
      setDirty(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: PAL.border, background: PAL.panel }}
         data-testid={`slide-editor-${lesson.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
          Slides ({slides.length})
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => slidesJob.start()} disabled={slidesJob.isActive}
                  className="text-xs rounded-full px-3 py-1"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid={`slides-regen-${lesson.id}`}>
            {slidesJob.isActive ? `Generating (${jobStatusLabel(slidesJob.job)})` : (slides.length ? "Regenerate all slides" : "Generate slides")}
          </button>
          <button onClick={() => imagesJob.start()} disabled={imagesJob.isActive || !slides.length}
                  className="text-xs rounded-full px-3 py-1"
                  style={{ background: "#7C3AED", color: "white" }}
                  data-testid={`slides-populate-visuals-${lesson.id}`}>
            {imagesJob.isActive ? `Populating (${jobStatusLabel(imagesJob.job)})` : "Populate visuals"}
          </button>
          <PreviewAsStudentButton courseId={course.id} lessonId={lesson.id}
                                     variant="ghost" testIdSuffix={`lesson-${lesson.id}`} />
          <button onClick={() => insertSlideAfter(slides.length - 1)}
                  className="text-xs rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: PAL.text }}
                  data-testid={`slides-add-${lesson.id}`}>
            + Add slide
          </button>
          <button onClick={save} disabled={!dirty || saving}
                  className="text-xs rounded-full px-3 py-1 disabled:opacity-40"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid={`slides-save-${lesson.id}`}>
            {saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {slides.map((s, i) => (
          <div key={i} className="rounded-lg p-3" style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
               data-testid={`slide-${lesson.id}-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs" style={{ color: PAL.muted }}>Slide {i + 1}</span>
              <button onClick={() => move(i, -1)} className="text-xs" style={{ color: PAL.accent }} data-testid={`slide-up-${lesson.id}-${i}`}>↑</button>
              <button onClick={() => move(i, 1)} className="text-xs" style={{ color: PAL.accent }} data-testid={`slide-down-${lesson.id}-${i}`}>↓</button>
              <button onClick={() => duplicate(i)} className="text-xs" style={{ color: PAL.accent }} data-testid={`slide-dup-${lesson.id}-${i}`}>Duplicate</button>
              <button onClick={() => insertSlideAfter(i)} className="text-xs" style={{ color: PAL.accent }} data-testid={`slide-insert-${lesson.id}-${i}`}>+ Insert after</button>
              <button onClick={() => deleteSlide(i)} className="text-xs ml-auto" style={{ color: "#F87171" }} data-testid={`slide-delete-${lesson.id}-${i}`}>Delete</button>
            </div>
            <input value={s.title || ""} onChange={(e) => patchSlide(i, { title: e.target.value })}
                   placeholder="Slide title"
                   className="w-full rounded bg-transparent border p-2 text-sm mb-2"
                   style={{ borderColor: PAL.border, color: PAL.text }}
                   data-testid={`slide-title-${lesson.id}-${i}`} />
            <textarea value={(s.bullets || []).join("\n")}
                       onChange={(e) => patchSlide(i, { bullets: e.target.value.split("\n").filter(Boolean) })}
                       placeholder="One bullet per line"
                       rows={4}
                       className="w-full rounded bg-transparent border p-2 text-sm mb-2"
                       style={{ borderColor: PAL.border, color: PAL.text }}
                       data-testid={`slide-bullets-${lesson.id}-${i}`} />
            <textarea value={s.speaker_notes || ""}
                       onChange={(e) => patchSlide(i, { speaker_notes: e.target.value })}
                       placeholder="Speaker notes (optional)"
                       rows={2}
                       className="w-full rounded bg-transparent border p-2 text-xs"
                       style={{ borderColor: PAL.border, color: PAL.muted }}
                       data-testid={`slide-notes-${lesson.id}-${i}`} />
            <input value={s.image_url || ""} onChange={(e) => patchSlide(i, { image_url: e.target.value })}
                   placeholder="Image URL (or use Populate visuals above)"
                   className="w-full rounded bg-transparent border p-2 text-xs mt-2"
                   style={{ borderColor: PAL.border, color: PAL.muted }}
                   data-testid={`slide-image-${lesson.id}-${i}`} />
            <SlideVisualPanel slide={s} onAction={slideAction} lesson={lesson} />
          </div>
        ))}
        {slides.length === 0 && (
          <div className="text-xs" style={{ color: PAL.muted }}>
            No slides yet. Click <b>Generate slides</b> above.
          </div>
        )}
      </div>
    </div>
  );
}


function SlideVisualPanel({ slide, onAction, lesson }) {
  const [replaceUrl, setReplaceUrl] = useState("");
  const status = slide?.image_status || "";
  const source = slide?.image_source || "";
  const isPlaceholder = source === "placeholder";
  const isApproved = status === "approved";
  const isTextOnly = status === "text_only_approved";
  const failureReason = slide?.failure_reason || "";
  const diag = slide?.diagnostic_reference || "";
  const provider = slide?.provider || "";

  if (!slide?.id) {
    return (
      <div className="mt-2 text-[10px]" style={{ color: PAL.muted }}>
        Save slides once to activate visual controls.
      </div>
    );
  }

  const badgeColor = isApproved
    ? "#10B981"
    : isTextOnly
      ? "#94A3B8"
      : status === "needs_review"
        ? "#F59E0B"
        : "#64748B";

  return (
    <div className="mt-3 rounded-lg p-3"
         style={{ background: "#0B0710", border: `1px solid ${PAL.border}` }}
         data-testid={`slide-visual-panel-${lesson.id}-${slide.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-[10px] uppercase tracking-widest font-semibold"
              style={{ color: badgeColor }}
              data-testid={`slide-visual-status-${slide.id}`}>
          {status || "no visual"}
        </span>
        {source && (
          <span className="text-[10px] px-2 py-0.5 rounded-full"
                style={{ background: PAL.accentSoft, color: PAL.text }}
                data-testid={`slide-visual-source-${slide.id}`}>
            {source.replace(/_/g, " ")}
          </span>
        )}
        {provider && (
          <span className="text-[10px]" style={{ color: PAL.muted }}>via {provider}</span>
        )}
        {diag && (
          <span className="text-[10px] font-mono" style={{ color: PAL.muted }}>
            ref {diag}
          </span>
        )}
        {slide?.requires_visual && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">
            Requires visual
          </span>
        )}
      </div>

      {isPlaceholder && (
        <div className="text-[11px] mb-2 rounded p-2"
             style={{ background: "#1F1414", color: "#FCA5A5", border: "1px solid #6B1D1D" }}
             data-testid={`slide-visual-failure-${slide.id}`}>
          Visual could not be populated automatically. Review, replace, regenerate, or approve this slide as text-only.
          {failureReason && <div className="opacity-80 mt-1">Reason: {failureReason}</div>}
        </div>
      )}

      {slide.image_url && !isTextOnly && (
        <div className="mb-2">
          <img src={slide.image_url} alt={slide.image_alt || ""}
               className="max-h-32 rounded border"
               style={{ borderColor: PAL.border }}
               data-testid={`slide-visual-preview-${slide.id}`} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <input value={slide.image_alt || ""}
               onChange={(e) => onAction(slide.id, "edit_alt_caption", { image_alt: e.target.value })}
               placeholder="Alt text (accessibility)"
               className="rounded bg-transparent border p-2 text-xs"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid={`slide-visual-alt-${slide.id}`} />
        <input value={slide.image_caption || ""}
               onChange={(e) => onAction(slide.id, "edit_alt_caption", { image_caption: e.target.value })}
               placeholder="Caption"
               className="rounded bg-transparent border p-2 text-xs"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid={`slide-visual-caption-${slide.id}`} />
      </div>

      <div className="flex items-center gap-2 mb-2">
        <input value={replaceUrl}
               onChange={(e) => setReplaceUrl(e.target.value)}
               placeholder="Paste image URL to attach or replace"
               className="flex-1 rounded bg-transparent border p-2 text-xs"
               style={{ borderColor: PAL.border, color: PAL.text }}
               data-testid={`slide-visual-replace-url-${slide.id}`} />
        <button
          type="button"
          disabled={!replaceUrl.trim()}
          onClick={async () => {
            await onAction(slide.id, "attach_image",
              { image_url: replaceUrl.trim(), image_source: "instructor_upload" });
            setReplaceUrl("");
          }}
          className="text-[11px] rounded-full px-3 py-1 disabled:opacity-40"
          style={{ background: PAL.accent, color: "#0F0B14" }}
          data-testid={`slide-visual-attach-${slide.id}`}
        >
          Attach
        </button>
      </div>

      <InstructorLibraryPicker slide={slide} onAction={onAction} lesson={lesson} />

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button"
                onClick={() => onAction(slide.id, "approve_visual")}
                disabled={isApproved || !slide.image_url || isPlaceholder}
                className="text-[11px] rounded-full px-3 py-1 disabled:opacity-40"
                style={{ background: "#10B981", color: "white" }}
                data-testid={`slide-visual-approve-${slide.id}`}>
          {isApproved ? "Visual approved" : "Approve visual"}
        </button>
        <button type="button"
                onClick={() => onAction(slide.id, "mark_text_only")}
                disabled={isTextOnly}
                className="text-[11px] rounded-full px-3 py-1 border disabled:opacity-40"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid={`slide-visual-text-only-${slide.id}`}>
          {isTextOnly ? "Text-only approved" : "Mark text-only"}
        </button>
        <button type="button"
                onClick={() => onAction(slide.id, "retry_visual")}
                className="text-[11px] rounded-full px-3 py-1 border"
                style={{ borderColor: PAL.border, color: PAL.text }}
                data-testid={`slide-visual-retry-${slide.id}`}>
          Retry visual
        </button>
        {isPlaceholder && (
          <button type="button"
                  onClick={() => onAction(slide.id, "remove_placeholder")}
                  className="text-[11px] rounded-full px-3 py-1 border"
                  style={{ borderColor: PAL.border, color: "#F87171" }}
                  data-testid={`slide-visual-remove-${slide.id}`}>
            Remove placeholder
          </button>
        )}
        <button type="button"
                onClick={() => onAction(slide.id, "set_requires_visual",
                                          { requires_visual: !slide.requires_visual })}
                className="text-[11px] rounded-full px-3 py-1 border ml-auto"
                style={{ borderColor: PAL.border, color: PAL.muted }}
                data-testid={`slide-visual-toggle-required-${slide.id}`}>
          {slide.requires_visual ? "Optional visual" : "Require visual"}
        </button>
      </div>
    </div>
  );
}


function InstructorLibraryPicker({ slide, onAction, lesson }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "approved", limit: "60" });
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/kcis/admin/kape/instructor-assets?${params}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
      setItems([]);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { if (open && items === null) load(); }, [open, items, load]);

  const attach = async (a) => {
    await onAction(slide.id, "attach_image", {
      image_url: a.url, image_alt: a.alt_text || slide.image_alt,
      image_caption: a.caption || slide.image_caption,
      image_source: "instructor_asset",
      image_license: a.license || "instructor-owned",
      provider: "instructor_library",
    });
    setOpen(false);
  };

  return (
    <div className="mb-2" data-testid={`slide-library-picker-${slide.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)}
              className="text-[11px] rounded-full px-3 py-1 border"
              style={{ borderColor: PAL.border, color: PAL.accent }}
              data-testid={`slide-search-library-${slide.id}`}>
        {open ? "Hide library" : "Search Media Library"}
      </button>
      {open && (
        <div className="mt-2 rounded-lg p-2"
             style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
             data-testid={`slide-library-panel-${slide.id}`}>
          <div className="flex items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                   placeholder="Search title / keyword / topic"
                   className="flex-1 rounded p-2 text-xs bg-transparent border"
                   style={{ borderColor: PAL.border, color: PAL.text }}
                   data-testid={`slide-library-q-${slide.id}`} />
            <button onClick={load}
                    className="text-[11px] rounded-full px-3 py-1"
                    style={{ background: PAL.accent, color: "#0F0B14" }}
                    data-testid={`slide-library-run-${slide.id}`}>
              Search
            </button>
          </div>
          {loading && <div className="text-[11px] mt-2" style={{ color: PAL.muted }}>Loading…</div>}
          {items && items.length === 0 && (
            <div className="text-[11px] mt-2" style={{ color: PAL.muted }}>
              No approved assets match. Try uploading via
              <a href="/kcis/admin/kape/instructor-assets" target="_blank" rel="noopener noreferrer"
                 className="ml-1 underline" style={{ color: PAL.accent }}>Instructor Assets Library</a>.
            </div>
          )}
          {items && items.length > 0 && (
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2"
                 data-testid={`slide-library-results-${slide.id}`}>
              {items.map((a) => (
                <button key={a.id} type="button" onClick={() => attach(a)}
                        className="rounded overflow-hidden text-left hover:ring-2"
                        style={{ background: "#000", ringColor: PAL.accent }}
                        title={a.title}
                        data-testid={`slide-library-pick-${slide.id}-${a.id}`}>
                  <img src={a.url?.startsWith("http") ? a.url : `${backend}${a.url}`}
                       alt={a.alt_text || a.title || ""}
                       className="w-full aspect-square object-cover" />
                  <div className="p-1 text-[10px] truncate" style={{ color: PAL.text }}>
                    {a.title}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
